import { type QueryClient } from '@tanstack/react-query';
import { sesionQueryOptions } from './useSesion';

/** Prefijo `diario-` para todo canal de `BroadcastChannel` del proyecto (aísla el nombre de
 * otras apps que pudieran compartir origen en desarrollo). */
const NOMBRE_CANAL = 'diario-sesion';

export type EventoSesion = 'entrada' | 'salida';

interface MensajeSesion {
	evento: EventoSesion;
}

/**
 * Caso límite CL-4 del cliente (documento 02b, §12, p. 10): «Dos pestañas, cierra sesión en
 * una → la otra se entera». Con la cookie de sesión `httpOnly` no existe el escuchador de
 * cambios de sesión de Supabase (`onAuthStateChange`) en el navegador, así que se resuelve a
 * mano con `BroadcastChannel`: quien entra o sale emite un mensaje aquí, y `suscribirseACambiosSesion`
 * lo recibe en el resto de pestañas para invalidar su copia de la sesión.
 *
 * Debe degradar sin romper donde `BroadcastChannel` no existe (jsdom sin polyfill, navegadores
 * muy antiguos): se comprueba antes de usarlo.
 */
export const emitirCambioSesion = (evento: EventoSesion): void => {
	if (typeof BroadcastChannel === 'undefined') {
		return;
	}
	const canal = new BroadcastChannel(NOMBRE_CANAL);
	canal.postMessage({ evento } satisfies MensajeSesion);
	canal.close();
};

/**
 * Se suscribe al canal de sesión: cuando OTRA pestaña avisa de una entrada o salida, actualiza la
 * query de sesión de esta pestaña para que TanStack Query la vuelva a pedir a `GET /me`. Pensada
 * para llamarse desde un `useEffect` en el layout raíz (oleada 3); devuelve siempre una función de
 * limpieza, incluso cuando `BroadcastChannel` no existe (en ese caso, no hace nada).
 *
 * `'salida'` usa `resetQueries`, no `invalidateQueries` — mismo motivo que `useSalir` (ver su
 * JSDoc en `useAcceso.ts`): con `invalidateQueries`, el reductor de error de TanStack Query
 * conserva la sesión vieja en `data` cuando el refetch recibe 401, así que la pestaña que solo
 * escucha seguiría enseñando esa sesión zombi. `'entrada'` sí sigue con `invalidateQueries`: aquí
 * no hay ningún dato viejo que purgar a la fuerza, y conservar `data` hasta que el refetch
 * confirme la sesión nueva evita un parpadeo a "sin sesión" en esta pestaña.
 */
export const suscribirseACambiosSesion = (queryClient: QueryClient): (() => void) => {
	if (typeof BroadcastChannel === 'undefined') {
		return () => {};
	}
	const canal = new BroadcastChannel(NOMBRE_CANAL);
	canal.onmessage = (mensaje: MessageEvent<MensajeSesion>) => {
		if (mensaje.data.evento === 'salida') {
			void queryClient.resetQueries({ queryKey: sesionQueryOptions.queryKey });
			return;
		}
		void queryClient.invalidateQueries({ queryKey: sesionQueryOptions.queryKey });
	};
	return () => canal.close();
};
